/**
 * Weekly fuel price intake (Control Centre Fuel).
 * PDF text-layer regex extract or manual entry → declared ingest
 * (POST /ingest/declared/keyfuels-diesel-price). History loads from
 * FuelPriceDeclared (Iceberg) so past weeks survive refresh.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Loader2, PoundSterling, RefreshCw } from 'lucide-react';
import { CHART_PALETTE } from '../../config/colors';
import { parseKeyfuelsDieselPriceFromPdf } from '../../utils/keyfuelsFuelPricePdf';
import {
  assertPdfFileSize,
  fileToBase64,
  uploadKeyfuelsDieselPrice,
} from '../../utils/fuelPriceUpload';
import { isValidCalendarYmd } from '../../utils/calendarYmd';
import { KEYFUELS_DIESEL_PRICE_MAX_PPL } from '../../utils/fuelPriceLimits';
import {
  trimPriceHistoryPreview,
  upsertPriceHistory,
  type FuelPriceSource,
  type FuelPriceWeekEntry,
} from '../../utils/fuelPriceHistory';
import {
  fetchDeclaredFuelPrices,
  mergeFuelPriceHistories,
} from '../../utils/fuelPriceHistoryLoad';

export type { FuelPriceSource, FuelPriceWeekEntry };
export { upsertPriceHistory };

type EntryMode = 'pdf' | 'manual';

const MOCK_PRICE_HISTORY: FuelPriceWeekEntry[] = [
  { weekOf: '2026-09-08', pencePerLitre: 142.6, source: 'inferred' },
  { weekOf: '2026-09-01', pencePerLitre: 142.6, source: 'pdf', filename: 'fuel-price_w36_2026.pdf' },
  { weekOf: '2026-08-25', pencePerLitre: 139.1, source: 'pdf' },
  { weekOf: '2026-08-18', pencePerLitre: 140.8, source: 'manual' },
];

function sourceLabel(source: FuelPriceSource): string {
  if (source === 'pdf') return 'PDF extract';
  if (source === 'manual') return 'Manual';
  return 'Preview (not saved)';
}

function sourceTagClass(source: FuelPriceSource): string {
  if (source === 'pdf') return 'bg-chart-5/15 text-chart-5 border-chart-5/30';
  if (source === 'manual') return 'bg-warning/15 text-warning border-warning/30';
  return 'bg-provenance-inferred/15 text-provenance-inferred border-provenance-inferred/30';
}

function formatWeekLabel(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

/** Monday (UTC) of the calendar week containing `asOf` — matches Keyfuels week-commencing. */
function currentWeekCommencingYmd(asOf: Date = new Date()): string {
  const y = asOf.getFullYear();
  const m = asOf.getMonth();
  const day = asOf.getDate();
  const utc = new Date(Date.UTC(y, m, day));
  const dow = utc.getUTCDay(); // 0=Sun … 6=Sat
  const toMonday = dow === 0 ? -6 : 1 - dow;
  utc.setUTCDate(utc.getUTCDate() + toMonday);
  return utc.toISOString().slice(0, 10);
}

function formatDeltaPpl(delta: number | null): string | null {
  if (delta == null || Math.abs(delta) < 0.05) return null;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}`;
}

interface FuelWeeklyPricePanelProps {
  useMockData?: boolean;
  /** A price was saved (queued for ingest). */
  onSaved?: () => void;
}

export default function FuelWeeklyPricePanel({ useMockData = false, onSaved }: FuelWeeklyPricePanelProps) {
  const [mode, setMode] = useState<EntryMode>('pdf');
  const [history, setHistory] = useState<FuelPriceWeekEntry[]>(
    () => (useMockData ? MOCK_PRICE_HISTORY : []),
  );
  const [rateInput, setRateInput] = useState('');
  const [weekInput, setWeekInput] = useState('');
  const [matchedLine, setMatchedLine] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  /** Values from the last successful PDF extract; used to detect user corrections. */
  const [extracted, setExtracted] = useState<{ rate: number; week: string } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseWarning, setParseWarning] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(!useMockData);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stageGenRef = useRef(0);
  /** Bumped by every history load so a slow one can't overwrite a newer result. */
  const historyGenRef = useRef(0);
  const softReloadTimeoutRef = useRef<number | null>(null);

  const clearSoftReload = () => {
    if (softReloadTimeoutRef.current != null) {
      window.clearTimeout(softReloadTimeoutRef.current);
      softReloadTimeoutRef.current = null;
    }
  };

  const reloadHistory = async (opts?: { mergeLocal?: boolean }) => {
    if (useMockData) return;
    const gen = ++historyGenRef.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { response, json, entries } = await fetchDeclaredFuelPrices();
      if (gen !== historyGenRef.current) return;
      if (!response.ok) {
        throw new Error(
          [json.error, json.message].filter(Boolean).join(': ')
            || `Failed to load prices (HTTP ${response.status})`,
        );
      }
      setHistory((prev) => {
        if (opts?.mergeLocal) return mergeFuelPriceHistories(entries, prev);
        // Manual "Refresh": take server truth, but keep any locally-saved week
        // that is newer than the newest server row — ingest is async, so a week
        // saved seconds ago may not be in Iceberg yet and must not vanish.
        const newestServerWeek = entries.reduce(
          (max, e) => (e.source !== 'inferred' && e.weekOf > max ? e.weekOf : max),
          '',
        );
        const pendingLocal = prev.filter(
          (e) => e.source !== 'inferred' && e.weekOf > newestServerWeek,
        );
        return pendingLocal.length > 0
          ? mergeFuelPriceHistories(entries, pendingLocal)
          : trimPriceHistoryPreview(entries);
      });
    } catch (err) {
      if (gen === historyGenRef.current) {
        setHistoryError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (gen === historyGenRef.current) setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (useMockData) return;
    const gen = ++historyGenRef.current;
    setHistoryLoading(true);
    void (async () => {
      try {
        const { response, json, entries } = await fetchDeclaredFuelPrices();
        if (gen !== historyGenRef.current) return;
        if (!response.ok) {
          throw new Error(
            [json.error, json.message].filter(Boolean).join(': ')
              || `Failed to load prices (HTTP ${response.status})`,
          );
        }
        // Merge with any optimistic weeks saved while this fetch was in flight.
        setHistory((prev) => mergeFuelPriceHistories(entries, prev));
        setHistoryError(null);
      } catch (err) {
        if (gen !== historyGenRef.current) return;
        setHistoryError(err instanceof Error ? err.message : String(err));
      } finally {
        if (gen === historyGenRef.current) setHistoryLoading(false);
      }
    })();
    return () => {
      // Invalidate any in-flight load so it can't setState after unmount.
      historyGenRef.current += 1;
      clearSoftReload();
    };
  }, [useMockData]);

  const displayHistory = useMemo(() => trimPriceHistoryPreview(history), [history]);
  const thisWeekOf = useMemo(() => currentWeekCommencingYmd(), []);

  /** Column-aligned sparkline: one equal-width column per week so labels sit under dots. */
  const chartModel = useMemo(() => {
    const rows = [...displayHistory].sort((a, b) => a.weekOf.localeCompare(b.weekOf)).slice(-5);
    if (rows.length === 0) return null;

    const values = rows.map((r) => r.pencePerLitre);
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const pad = Math.max(2, (dataMax - dataMin) * 0.35 || 2);
    const yMin = dataMin - pad;
    const yMax = dataMax + pad;
    const span = yMax - yMin || 1;

    const n = rows.length;
    const plotH = 100; // SVG user units
    const padTop = 22;
    const padBottom = 10;
    const usableH = plotH - padTop - padBottom;

    const points = rows.map((r, i) => {
      const x = i + 0.5; // column centre in viewBox width = n
      const y = padTop + (1 - (r.pencePerLitre - yMin) / span) * usableH;
      const delta = i === 0 ? null : r.pencePerLitre - rows[i - 1].pencePerLitre;
      return { ...r, x, y, delta };
    });

    const linePath = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(3)},${p.y.toFixed(2)}`)
      .join(' ');

    return { points, linePath, n, plotH };
  }, [displayHistory]);

  const resetExtractFields = () => {
    setMatchedLine(null);
    setPdfName(null);
    setPdfFile(null);
    setExtracted(null);
    setParseError(null);
    setParseWarning(null);
    setSaveMessage(null);
  };

  const handlePdfFile = async (file: File) => {
    const gen = ++stageGenRef.current;
    setParsing(true);
    resetExtractFields();
    setPdfName(file.name);
    setPdfFile(file);
    try {
      assertPdfFileSize(file);
      if (useMockData) {
        if (gen !== stageGenRef.current) return;
        setRateInput('142.6');
        setWeekInput('2026-09-01');
        setMatchedLine(
          'Your Keyfuels Diesel fuel price for week commencing 01/09/26 is 142.60 pence per litre + VAT',
        );
        setExtracted({ rate: 142.6, week: '2026-09-01' });
        return;
      }
      const parsed = await parseKeyfuelsDieselPriceFromPdf(file);
      if (gen !== stageGenRef.current) return;
      setRateInput(String(parsed.pencePerLitre));
      setWeekInput(parsed.weekCommencingYmd);
      setMatchedLine(parsed.matchedLine);
      setExtracted({ rate: parsed.pencePerLitre, week: parsed.weekCommencingYmd });
      if (parsed.matchCount > 1) {
        setParseWarning(
          `Found ${parsed.matchCount} price lines — using the first. Check the week and rate before saving.`,
        );
      }
    } catch (err) {
      if (gen !== stageGenRef.current) return;
      setParseError(err instanceof Error ? err.message : String(err));
      setRateInput('');
      setWeekInput('');
      setPdfFile(null);
      setPdfName(null);
      setMatchedLine(null);
      setExtracted(null);
    } finally {
      if (gen === stageGenRef.current) {
        setParsing(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    setParseError(null);
    setSaveMessage(null);
    const rate = Number(rateInput);
    const weekOf = weekInput.trim();
    if (!Number.isFinite(rate) || rate <= 0) {
      setParseError('Enter a valid rate in pence per litre.');
      return;
    }
    if (rate > KEYFUELS_DIESEL_PRICE_MAX_PPL) {
      setParseError(`Rate must be ≤ ${KEYFUELS_DIESEL_PRICE_MAX_PPL} ppl.`);
      return;
    }
    if (!isValidCalendarYmd(weekOf)) {
      setParseError('Applies week of must be a real calendar date (YYYY-MM-DD).');
      return;
    }

    const fieldsMatchExtract =
      extracted != null
      && Math.abs(rate - extracted.rate) < 1e-9
      && weekOf === extracted.week;

    // Editing extract after PDF parse → manual (do not claim pdf_regex provenance).
    const usePdfRegex = mode === 'pdf' && fieldsMatchExtract && Boolean(pdfFile || useMockData);
    const source: FuelPriceSource = usePdfRegex ? 'pdf' : 'manual';

    if (mode === 'pdf' && !useMockData && !pdfFile) {
      setParseError('Drop the Keyfuels PDF again before saving so the raw file can be stored.');
      return;
    }

    if (useMockData) {
      setHistory((prev) =>
        upsertPriceHistory(prev, {
          weekOf,
          pencePerLitre: rate,
          source,
          matchedLine: usePdfRegex ? matchedLine ?? undefined : undefined,
          filename: pdfName ?? undefined,
        }),
      );
      setSaveMessage('Demo mode — saved locally only (no ingest).');
      return;
    }

    setSaving(true);
    try {
      const { response, json } = await uploadKeyfuelsDieselPrice({
        week_commencing: weekOf,
        pence_per_litre: rate,
        entry_method: usePdfRegex ? 'pdf_regex' : 'manual',
        matched_line: usePdfRegex ? matchedLine ?? undefined : undefined,
        filename: pdfName ?? undefined,
        pdfBase64: pdfFile ? await fileToBase64(pdfFile) : undefined,
      });

      if (!response.ok) {
        throw new Error(json.error || json.message || `Save failed with HTTP ${response.status}`);
      }

      setHistory((prev) =>
        upsertPriceHistory(prev, {
          weekOf,
          pencePerLitre: rate,
          source,
          matchedLine: usePdfRegex ? matchedLine ?? undefined : undefined,
          filename: pdfName ?? undefined,
        }),
      );
      setSaveMessage(
        usePdfRegex
          ? `Queued — raw PDF stored and rate ${rate} ppl for week of ${weekOf}.`
          : `Queued — manual rate ${rate} ppl for week of ${weekOf}.`,
      );
      onSaved?.();
      // Ingest is async — soft-sync from Iceberg shortly after queue.
      clearSoftReload();
      softReloadTimeoutRef.current = window.setTimeout(() => {
        softReloadTimeoutRef.current = null;
        void reloadHistory({ mergeLocal: true });
      }, 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const timedOut = /timed out|TimeoutError|aborted/i.test(msg);
      setParseError(
        timedOut
          ? 'Save timed out while ZeeCore stored the price. Try again, or check that ZeeCore is running.'
          : msg,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card/50 rounded-lg border border-border p-5 flex flex-col min-h-0 h-full">
      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
        <PoundSterling className="w-4 h-4 text-chart-2" />
        Weekly fuel price
      </h4>
      <p className="text-xs text-muted-foreground mt-1 mb-3">
        Sets the ppl rate for that week.
      </p>

      <div className="mb-3 flex items-center gap-2">
        <div
          role="tablist"
          aria-label="Price entry mode"
          className="inline-flex w-fit rounded-lg border border-border bg-background/70 p-1"
        >
          {([
            { id: 'pdf' as const, label: 'Upload PDF' },
            { id: 'manual' as const, label: 'Manual entry' },
          ]).map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={mode === opt.id}
              onClick={() => {
                setMode(opt.id);
                resetExtractFields();
                setParseError(null);
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                mode === opt.id
                  ? 'bg-chart-2 text-white'
                  : 'text-foreground hover:text-white hover:bg-secondary/80'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void reloadHistory({ mergeLocal: false })}
          disabled={useMockData || historyLoading}
          title="Reload saved weekly prices from ZeeCore"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border bg-background/70 px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary/80 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${historyLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {mode === 'pdf' && (
        <div
          role="button"
          tabIndex={0}
          className={`group relative mb-3 cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center transition-all duration-250 ease-out ${
            parsing ? 'pointer-events-none opacity-60' : ''
          } ${
            isDragging || pdfFile
              ? 'border-solid border-chart-2 bg-background'
              : 'border-border bg-background hover:border-chart-2'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) void handlePdfFile(file);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handlePdfFile(file);
            }}
          />
          <div
            className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full border-2 transition-all duration-250 ${
              isDragging || pdfFile
                ? 'border-chart-2 bg-background'
                : 'border-border bg-background group-hover:border-chart-2'
            }`}
          >
            <FileText className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-base font-bold text-white mb-1">Drop supplier price PDF</p>
          <p className="text-sm text-muted-foreground mb-0">
            Drag and drop, or click to browse
          </p>
          {parsing && (
            <p className="text-xs text-chart-2 mt-3 inline-flex items-center gap-1.5 justify-center">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Reading PDF…
            </p>
          )}
          {pdfName && !parsing && (
            <p className="text-xs text-chart-2/90 mt-3 font-medium truncate px-2">{pdfName}</p>
          )}
        </div>
      )}

      <div className="rounded-lg border border-chart-5/30 bg-chart-5/10 p-3 space-y-3">
        <p className="text-xs text-muted-foreground">
          {mode === 'pdf'
            ? matchedLine
              ? (
                <>
                  Extracted from{' '}
                  <span className="text-foreground font-medium">{pdfName ?? 'PDF'}</span>
                  {extracted
                    && (Math.abs(Number(rateInput) - extracted.rate) >= 1e-9
                      || weekInput.trim() !== extracted.week)
                    ? (
                      <span className="text-warning"> — edited; will save as manual</span>
                    )
                    : null}
                </>
              )
              : 'Drop a PDF to prefill rate and week, then confirm Save'
            : 'Entered manually — flagged as manual in history'}
        </p>
        {matchedLine && (
          <p className="font-mono text-xs text-foreground bg-background/60 border border-border rounded px-2 py-1.5 break-words">
            {matchedLine}
          </p>
        )}
        <div className="flex flex-wrap gap-2 items-end">
          <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground flex-1 min-w-28">
            Rate (ppl)
            <input
              type="text"
              inputMode="decimal"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              placeholder="e.g. 142.6"
              className="bg-background border border-border text-white rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-chart-2/50"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground flex-1 min-w-32">
            Applies week of
            <input
              type="date"
              value={weekInput}
              onChange={(e) => setWeekInput(e.target.value)}
              className="bg-background border border-border text-white rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-chart-2/50"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || parsing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-md bg-chart-2 hover:bg-chart-2 disabled:bg-secondary disabled:text-muted-foreground text-white shrink-0"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      {historyLoading && (
        <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading saved weekly prices…
        </p>
      )}
      {historyError && (
        <p className="text-xs text-warning mt-2">
          Could not reload saved prices: {historyError}
        </p>
      )}
      {parseWarning && (
        <p className="text-xs text-warning mt-2">{parseWarning}</p>
      )}
      {parseError && (
        <p className="text-xs text-destructive mt-2">{parseError}</p>
      )}
      {saveMessage && (
        <p className="text-xs text-success mt-2">{saveMessage}</p>
      )}

      {chartModel && (
        <div className="mt-4">
          <div className="rounded-lg border border-border/80 bg-background/40 px-1 pt-2 pb-1.5">
            <div className="relative h-28 w-full">
              {/* Soft wash behind the current week column only */}
              <div className="pointer-events-none absolute inset-0 flex" aria-hidden>
                {chartModel.points.map((p) => (
                  <div
                    key={`bg-${p.weekOf}`}
                    className={`flex-1 rounded-sm ${
                      p.weekOf === thisWeekOf ? 'bg-chart-2/[0.08]' : ''
                    }`}
                  />
                ))}
              </div>
              <svg
                viewBox={`0 0 ${chartModel.n} ${chartModel.plotH}`}
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full overflow-visible"
                role="img"
                aria-label="Weekly fuel price trend"
              >
                <path
                  d={chartModel.linePath}
                  fill="none"
                  stroke={CHART_PALETTE[2]}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </svg>
              {/* Dots + rate labels: same flex columns as the week labels below */}
              <div className="pointer-events-none absolute inset-0 flex">
                {chartModel.points.map((p) => {
                  const isThisWeek = p.weekOf === thisWeekOf;
                  return (
                    <div key={`pt-${p.weekOf}`} className="relative flex-1">
                      <span
                        className={`absolute left-1/2 text-xs font-semibold tabular-nums ${
                          isThisWeek ? 'text-chart-2' : 'text-foreground'
                        }`}
                        style={{
                          top: `${(p.y / chartModel.plotH) * 100}%`,
                          transform: 'translate(-50%, calc(-100% - 6px))',
                        }}
                      >
                        {p.pencePerLitre.toFixed(1)}
                      </span>
                      <span
                        className={`absolute left-1/2 rounded-full border ${
                          isThisWeek ? 'h-3 w-3' : 'h-2.5 w-2.5'
                        } ${
                          p.source === 'inferred'
                            ? isThisWeek
                              ? 'border-chart-2 bg-provenance-inferred ring-2 ring-chart-2/40'
                              : 'border-background bg-provenance-inferred'
                            : isThisWeek
                              ? 'border-chart-2 bg-success ring-2 ring-chart-2/40'
                              : 'border-background bg-success'
                        }`}
                        style={{
                          top: `${(p.y / chartModel.plotH) * 100}%`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="mt-1.5 flex w-full">
              {chartModel.points.map((p) => {
                const deltaLabel = formatDeltaPpl(p.delta);
                const isThisWeek = p.weekOf === thisWeekOf;
                return (
                  <div key={`lbl-${p.weekOf}`} className="min-w-0 flex-1 px-0.5 text-center">
                    <div
                      className={`text-xs whitespace-nowrap ${
                        isThisWeek ? 'font-semibold text-chart-2' : 'text-muted-foreground'
                      }`}
                    >
                      {formatWeekLabel(p.weekOf)}
                      {isThisWeek ? (
                        <span className="ml-1 text-xs font-bold uppercase tracking-wide text-chart-2/90">
                          Now
                        </span>
                      ) : null}
                    </div>
                    {deltaLabel ? (
                      <div
                        className={`text-xs font-semibold tabular-nums ${
                          p.delta != null && p.delta > 0 ? 'text-destructive' : 'text-success'
                        }`}
                      >
                        {deltaLabel}
                      </div>
                    ) : (
                      <div className="text-xs text-transparent">·</div>
                    )}
                    <div
                      className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-xs font-bold ${
                        p.source === 'inferred'
                          ? 'bg-provenance-inferred/15 text-provenance-inferred'
                          : 'bg-success/15 text-success'
                      }`}
                    >
                      {p.source === 'inferred' ? 'Preview' : 'Confirmed'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 max-h-price-list-5 overflow-y-auto overflow-x-auto rounded-md border border-border/60">
        <table className="w-full text-xs table-fixed">
          <thead className="sticky top-0 bg-background z-raised">
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border h-8">
              <th className="py-0 pr-2 pl-2 font-semibold">Week of</th>
              <th className="py-0 pr-2 font-semibold">Source</th>
              <th className="py-0 pr-2 font-semibold text-right">Rate (ppl)</th>
            </tr>
          </thead>
          <tbody>
            {displayHistory.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-4 text-muted-foreground text-center">
                  {historyLoading ? 'Loading…' : 'No weekly prices saved yet'}
                </td>
              </tr>
            ) : (
              displayHistory.map((row) => (
                <tr key={`${row.weekOf}-${row.source}`} className="border-b border-border/80 h-9">
                  <td className="py-0 pr-2 pl-2 text-foreground align-middle">{row.weekOf}</td>
                  <td className="py-0 pr-2 align-middle">
                    <span className={`inline-flex text-xs font-bold px-2 py-0.5 rounded border ${sourceTagClass(row.source)}`}>
                      {sourceLabel(row.source)}
                    </span>
                  </td>
                  <td className="py-0 pr-2 text-right tabular-nums text-white font-medium align-middle">
                    {row.pencePerLitre.toFixed(1)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
